import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';

import { selectIsSubCompanyUser } from 'store/auth/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import { selectIsDesktop } from 'store/global/selectors';

import { LOAN_ACTION_TRIGGER_EVENT } from '../../../UniversalSearch/UniversalSearch.constants';
import {
  hasExecutionPrompt,
  useLoanActionsControls,
} from '../../LoanInfoHeader/LoanActionsControls';
import LoanActionRunnerDrawers from '../../LoanInfoHeader/LoanActionsControls/LoanActionRunnerDrawers';

import { LoanActionsControlsContext, useGroupedLoanActions } from './hooks';
import { isSendFormBundleAction } from './LoanActionsControls.utils';

const LoanActionsControlsProvider = ({ children, setIsLoading }) => {
  const dispatch = useDispatch();
  const isDesktop = useSelector(selectIsDesktop);
  const isMobile = !isDesktop;
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const {
    executingActionId,
    isDataLoaded,
    onLoanActionClick,
    loanActions,
    disabledLoanActionsSet,
    runningLoanActionsSet,
    loanEditorCustomFormDrawerState,
    onCloseLoanEditorCustomFormDrawer,
    iframeDrawerState,
    onCloseIframeDrawer,
    worksheetDrawerState,
    onCloseWorksheetDrawer,
    mersPreviewDrawerState,
    onCloseMersPreviewDrawer,
  } = useLoanActionsControls(setIsLoading);

  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);

  const visibleLoanActions = useMemo(
    () =>
      isSubCompanyUser
        ? (loanActions ?? []).filter(action => !isSendFormBundleAction(action))
        : loanActions,
    [loanActions, isSubCompanyUser],
  );

  const groupedActions = useGroupedLoanActions(visibleLoanActions);

  const loanActionsById = useMemo(() => _.keyBy(loanActions ?? [], 'id'), [loanActions]);

  const onConfirmLoanAction = useCallback(
    e => {
      const { id: actionId, name: actionName } = e.currentTarget.dataset;

      if (hasExecutionPrompt(loanActionsById[actionId])) {
        onLoanActionClick(actionId);

        return;
      }

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Action execution confirmation',
        description: `Do you want to proceed with the following action: ${
          actionName || e.currentTarget.name
        }?`,
        onAccept: () => {
          onLoanActionClick(actionId);
        },
        onAcceptButtonName: 'Proceed',
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [dispatch, onLoanActionClick, loanActionsById],
  );

  const onOpenMobileDrawer = useCallback(() => setIsMobileDrawerOpen(true), []);
  const onCloseMobileDrawer = useCallback(() => setIsMobileDrawerOpen(false), []);

  const value = useMemo(
    () => ({
      isDataLoaded,
      isMobile,
      isMobileDrawerOpen,
      onOpenMobileDrawer,
      onCloseMobileDrawer,
      groupedActions,
      executingActionId,
      disabledLoanActionsSet,
      runningLoanActionsSet,
      onConfirmLoanAction,
      onLoanActionClick,
    }),
    [
      isDataLoaded,
      isMobile,
      isMobileDrawerOpen,
      onOpenMobileDrawer,
      onCloseMobileDrawer,
      groupedActions,
      executingActionId,
      disabledLoanActionsSet,
      runningLoanActionsSet,
      onConfirmLoanAction,
      onLoanActionClick,
    ],
  );

  useEffect(() => {
    const onSearchActionTrigger = event => {
      const { actionId, actionName } = event.detail;

      // A trigger for a disabled action must not vanish silently — tell the user why
      // nothing is going to run.
      if (disabledLoanActionsSet.has(actionId)) {
        dispatch(
          showMessageAction({
            message: `The "${actionName}" action is currently unavailable for this loan`,
            variant: 'warning',
          }),
        );

        return;
      }

      if (hasExecutionPrompt(loanActionsById[actionId])) {
        onLoanActionClick(actionId);

        return;
      }

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Action execution confirmation',
        description: `Do you want to proceed with the following action: ${actionName}?`,
        onAccept: () => {
          onLoanActionClick(actionId);
        },
        onAcceptButtonName: 'Proceed',
      };

      dispatch(openConfirmationDialogAction(params));
    };

    document.addEventListener(LOAN_ACTION_TRIGGER_EVENT, onSearchActionTrigger);

    return () => {
      document.removeEventListener(LOAN_ACTION_TRIGGER_EVENT, onSearchActionTrigger);
    };
  }, [dispatch, onLoanActionClick, disabledLoanActionsSet, loanActionsById]);

  return (
    <LoanActionsControlsContext.Provider value={value}>
      {children}

      <LoanActionRunnerDrawers
        iframeDrawerState={iframeDrawerState}
        loanEditorCustomFormDrawerState={loanEditorCustomFormDrawerState}
        mersPreviewDrawerState={mersPreviewDrawerState}
        worksheetDrawerState={worksheetDrawerState}
        onCloseIframeDrawer={onCloseIframeDrawer}
        onCloseLoanEditorCustomFormDrawer={onCloseLoanEditorCustomFormDrawer}
        onCloseMersPreviewDrawer={onCloseMersPreviewDrawer}
        onCloseWorksheetDrawer={onCloseWorksheetDrawer}
      />
    </LoanActionsControlsContext.Provider>
  );
};

LoanActionsControlsProvider.propTypes = {
  children: PropTypes.node,
  ...WithLoadingPropTypes,
};

export default withLoading(LoanActionsControlsProvider);
